import { useUiStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import {
  type FirstDayOfWeek,
  type MonthType,
  type OnDatesChangeProps,
  useDatepicker,
} from "@datepicker-react/hooks";

import {
  type DateTimeFormatStyle,
  getLang,
  useI18nContext,
} from "@hopper-b2b/i18n";
import { firstWeekDay } from "../../DateRangePicker";
import defaultStyles from "../ColumnView/ColumnView.module.scss";
import { Month } from "../Month";

export interface IGroupViewProps {
  minAllowedDate: Date | null;
  maxAllowedDate: Date | null;
  setFocusedMonthIndex?: (index: number) => void;
  numberOfMonths: number;
  priceTagsElement: JSX.Element;
  a11yModeEnabled?: boolean;
}

// temporarily disable linting here to prevent errors due to the placeholder function
export const GroupView = ({
  minAllowedDate,
  maxAllowedDate,
  setFocusedMonthIndex,
  numberOfMonths,
  priceTagsElement,
  a11yModeEnabled,
}: IGroupViewProps) => {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleDateChange = (props: OnDatesChangeProps) => {}; // required by useDatepicker; month navigation is handled internally

  const { firstDayOfWeek, activeMonths } = useDatepicker({
    firstDayOfWeek: firstWeekDay(getLang()) as FirstDayOfWeek, // 0 is Sunday.
    startDate: minAllowedDate,
    endDate: maxAllowedDate,
    focusedInput: null,
    onDatesChange: handleDateChange,
    numberOfMonths,
  });

  const styles = useUiStyles(
    CoreUiComponents.DatePickerGroupView,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreUiComponents.DatePickerGroupView
  );
  const { DateTimeFormatStyle } = useI18nContext();

  return (
    <div className={block}>
      <div className={cn("price-range-tags-section")}>{priceTagsElement}</div>
      <div className={cn("months-container")}>
        {renderMonths(
          activeMonths,
          firstDayOfWeek,
          DateTimeFormatStyle.Custom({ month: "short" }),
          setFocusedMonthIndex,
          a11yModeEnabled
        )}
      </div>
    </div>
  );
};

const renderMonths = (
  months: MonthType[],
  firstDayOfWeek: FirstDayOfWeek,
  monthFormat: DateTimeFormatStyle,
  setFocusedMonthIndex?: (index: number) => void,
  a11yModeEnabled?: boolean
) => {
  const styles = useUiStyles(
    CoreUiComponents.DatePickerGroupView,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreUiComponents.DatePickerGroupView
  );

  return months.map((month: MonthType, index: number) => (
    <Month
      key={`${month.year}-${month.month}`}
      year={month.year}
      month={month.month}
      firstDayOfWeek={firstDayOfWeek}
      date={month.date}
      hideWeekdayLabels={true}
      rootClassName={cn("group-view-month-root")}
      headerClassName={cn("group-view-month-label")}
      bodyClassName={cn("group-view-month-body")}
      monthFormat={monthFormat}
      isMiniCalendar={true}
      onClick={() => setFocusedMonthIndex && setFocusedMonthIndex(index)}
      a11yModeEnabled={a11yModeEnabled}
    />
  ));
};
